import type { Scope } from './scope';

/**
 * Live cognitive-state indices derived from EEG band powers.
 *
 * All six formulas are textbook cognitive-neuroscience ratios; none
 * are proprietary.
 *
 *   Focus       = β / α                  attention / arousal
 *   Engagement  = β / (α + θ)            task engagement
 *   Workload    = θ / α                  cognitive load (TLX proxy)
 *   Stress      = β_high / α             cortical activation / anxiety
 *                                        (β_high = 20–30 Hz; avoids the
 *                                        EMG-contaminated γ band that
 *                                        moving operators pollute)
 *   Fatigue     = (α + θ) / β            slowed cortical activity
 *   Valence     = ln(α_R) − ln(α_L)      FAA, emotional valence
 *
 * Preprocessing per the Stanford pipeline:
 *   1. 60 Hz notch (US mains) — removes lighting-hum contamination of
 *      the β/γ bins
 *   2. De-mean — substitutes for their 0.5 Hz high-pass (DC removal
 *      is all we need since we discard bin 0 anyway)
 *   3. Hann window — reduces spectral leakage
 *   4. Cooley-Tukey radix-2 FFT — same as bandPowers.ts
 *   5. Integrate squared magnitude over each band's bins
 *   6. Compute index per channel, then average (Stanford convention)
 *
 * Band definitions match the Stanford pipeline rather than our existing
 * relative-power chart:
 *   delta 0.5–4   theta 4–7   alpha 8–12   beta 12–30   gamma 30–100
 *
 * Compute window: 1 second (256 samples at 256 Hz). The Stanford
 * pipeline uses 2 s; 1 s gives us more responsive live feedback at the
 * cost of higher variance per estimate.
 */

const EEG_SAMPLE_RATE = 256;
/** FFT window length — one second at 256 Hz, power of two for radix-2. */
const WINDOW = 256;

/** Mains-hum notch parameters. Change to 50 Hz for EU/UK power grids. */
const NOTCH_HZ = 60;
const NOTCH_Q = 30;

/**
 * Band edges in Hz. δ and the upper γ above 50 Hz aren't in any of
 * the formulas. β_high is a sub-band of β used by the Stress index
 * because the full 12–30 Hz β overlaps Focus (β/α) — Stress needs to
 * distinguish itself from Focus by reading higher-frequency
 * activation rather than the full β range.
 */
const BAND_THETA = { lo: 4, hi: 7 };
const BAND_ALPHA = { lo: 8, hi: 12 };
const BAND_BETA = { lo: 12, hi: 30 };
const BAND_BETA_HIGH = { lo: 20, hi: 30 };

/**
 * Channels used for the FAA (Frontal Alpha Asymmetry) calculation —
 * AF7 is the left frontal electrode, AF8 the right. The other two
 * Muse channels (TP9, TP10) are ear references and not used here.
 */
const FAA_LEFT_CHANNEL = 'eeg:AF7';
const FAA_RIGHT_CHANNEL = 'eeg:AF8';

const EPS = 1e-6;

/**
 * Clean-window gate. A channel's last second is left out of the indices
 * when its mean-removed RMS exceeds {@link MAX_CLEAN_RMS_UV} (off-skin /
 * floating electrode, motion burst) or any sample reaches
 * {@link RAIL_UV} (amplifier saturated — Athena rails at ±725 µV, Gen 2
 * is clamped at ±1000). Such a window has a flat, noise-shaped spectrum,
 * and since β spans 18 one-Hz bins against θ+α's 7, noise alone reads as
 * "zero fatigue / high focus". On ACS 2026 data 51% of channel-seconds
 * failed this gate; TP9/TP10 off the skin were the usual culprit.
 */
const MAX_CLEAN_RMS_UV = 100;
const RAIL_UV = 700;

export function isCleanWindow(samples: number[], start: number): boolean {
  let mean = 0;
  for (let i = 0; i < WINDOW; i++) {
    const v = samples[start + i];
    if (Math.abs(v) >= RAIL_UV) return false;
    mean += v;
  }
  mean /= WINDOW;
  let ss = 0;
  for (let i = 0; i < WINDOW; i++) {
    const d = samples[start + i] - mean;
    ss += d * d;
  }
  return Math.sqrt(ss / WINDOW) <= MAX_CLEAN_RMS_UV;
}

interface BiquadCoefs {
  b0: number;
  b1: number;
  b2: number;
  a1: number;
  a2: number;
}

/**
 * Robert Bristow-Johnson "Audio EQ Cookbook" notch biquad — what
 * scipy.signal.iirnotch produces. f0 in Hz, fs in Hz, q dimensionless.
 */
function designNotch(f0: number, fs: number, q: number): BiquadCoefs {
  const w0 = (2 * Math.PI * f0) / fs;
  const alpha = Math.sin(w0) / (2 * q);
  const cosW0 = Math.cos(w0);
  const a0 = 1 + alpha;
  return {
    b0: 1 / a0,
    b1: (-2 * cosW0) / a0,
    b2: 1 / a0,
    a1: (-2 * cosW0) / a0,
    a2: (1 - alpha) / a0,
  };
}

/**
 * Apply a biquad filter, single pass, direct form II transposed.
 * Writes into `out`; `input` and `out` may not alias.
 */
function applyBiquad(
  input: Float64Array,
  coefs: BiquadCoefs,
  out: Float64Array,
): void {
  let x1 = 0;
  let x2 = 0;
  let y1 = 0;
  let y2 = 0;
  for (let i = 0; i < input.length; i++) {
    const x0 = input[i];
    const y0 =
      coefs.b0 * x0 +
      coefs.b1 * x1 +
      coefs.b2 * x2 -
      coefs.a1 * y1 -
      coefs.a2 * y2;
    x2 = x1;
    x1 = x0;
    y2 = y1;
    y1 = y0;
    out[i] = y0;
  }
}

// Pre-computed notch coefficients, Hann window, and scratch buffers —
// the indices update at ~1 Hz so we'd rather not allocate on each call.
const NOTCH = designNotch(NOTCH_HZ, EEG_SAMPLE_RATE, NOTCH_Q);

const HANN = new Float64Array(WINDOW);
for (let i = 0; i < WINDOW; i++) {
  HANN[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (WINDOW - 1)));
}

const inputBuf = new Float64Array(WINDOW);
const filteredBuf = new Float64Array(WINDOW);
const fftReal = new Float64Array(WINDOW);
const fftImag = new Float64Array(WINDOW);

/** In-place Cooley-Tukey radix-2 FFT. Same as bandPowers.ts. */
function fftRadix2(real: Float64Array, imag: Float64Array): void {
  const n = real.length;
  let j = 0;
  for (let i = 1; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      const tr = real[i];
      const ti = imag[i];
      real[i] = real[j];
      imag[i] = imag[j];
      real[j] = tr;
      imag[j] = ti;
    }
  }
  for (let size = 2; size <= n; size <<= 1) {
    const half = size >> 1;
    const angle = (-2 * Math.PI) / size;
    const wReal = Math.cos(angle);
    const wImag = Math.sin(angle);
    for (let i = 0; i < n; i += size) {
      let cReal = 1;
      let cImag = 0;
      for (let k = 0; k < half; k++) {
        const tReal = cReal * real[i + k + half] - cImag * imag[i + k + half];
        const tImag = cReal * imag[i + k + half] + cImag * real[i + k + half];
        real[i + k + half] = real[i + k] - tReal;
        imag[i + k + half] = imag[i + k] - tImag;
        real[i + k] += tReal;
        imag[i + k] += tImag;
        const nReal = cReal * wReal - cImag * wImag;
        const nImag = cReal * wImag + cImag * wReal;
        cReal = nReal;
        cImag = nImag;
      }
    }
  }
}

/**
 * Sum |X[k]|² across the integer FFT bins in [loHz, hiHz). Bin k = k Hz
 * for our N=256 / fs=256 setup. Skip bin 0 (DC).
 */
function integrateBand(
  real: Float64Array,
  imag: Float64Array,
  loHz: number,
  hiHz: number,
): number {
  const loBin = Math.max(1, Math.ceil(loHz));
  const hiBin = Math.min(WINDOW / 2, Math.floor(hiHz));
  let p = 0;
  for (let k = loBin; k < hiBin; k++) {
    p += real[k] * real[k] + imag[k] * imag[k];
  }
  return p;
}

interface ChannelPowers {
  theta: number;
  alpha: number;
  beta: number;
  betaHigh: number;
}

/**
 * Compute band powers for one channel's most recent WINDOW samples.
 * Accepts a regular number[] (what {@link Scope.read} returns) and
 * copies into a Float64Array scratch buffer before filtering.
 * Mutates the module-level scratch buffers; caller is single-threaded.
 */
function powersForChannel(samples: number[], start: number): ChannelPowers {
  // 1. Copy into scratch, then notch-filter.
  for (let i = 0; i < WINDOW; i++) inputBuf[i] = samples[start + i];
  applyBiquad(inputBuf, NOTCH, filteredBuf);
  // 2. De-mean + Hann.
  let mean = 0;
  for (let i = 0; i < WINDOW; i++) mean += filteredBuf[i];
  mean /= WINDOW;
  for (let i = 0; i < WINDOW; i++) {
    fftReal[i] = (filteredBuf[i] - mean) * HANN[i];
    fftImag[i] = 0;
  }
  // 3. FFT and integrate the four bands the indices need (delta isn't
  // in any of the formulas, so skip it).
  fftRadix2(fftReal, fftImag);
  return {
    theta: integrateBand(fftReal, fftImag, BAND_THETA.lo, BAND_THETA.hi),
    alpha: integrateBand(fftReal, fftImag, BAND_ALPHA.lo, BAND_ALPHA.hi),
    beta: integrateBand(fftReal, fftImag, BAND_BETA.lo, BAND_BETA.hi),
    betaHigh: integrateBand(
      fftReal,
      fftImag,
      BAND_BETA_HIGH.lo,
      BAND_BETA_HIGH.hi,
    ),
  };
}

export interface CognitiveIndices {
  /** β / α — attention / arousal proxy. */
  focus: number;
  /** β / (α + θ) — task-engagement proxy. */
  engagement: number;
  /** θ / α — cognitive workload / mental effort (TLX proxy). */
  workload: number;
  /**
   * β_high / α (β_high = 20–30 Hz) — cortical activation / anxious
   * arousal. Distinct from Focus (which uses full β / α) because the
   * upper β sub-band lights up more selectively under acute load.
   * Originally tried γ / α but γ on a Muse is dominated by EMG
   * artefacts when the operator is moving, so the ratio pinned.
   */
  stress: number;
  /**
   * (α + θ) / β — slowed cortical activity. Higher ≈ more fatigued
   * (the inverse direction of focus).
   */
  fatigue: number;
  /**
   * ln(α_AF8) − ln(α_AF7) — frontal alpha asymmetry. Positive ≈
   * approach / positive affect; negative ≈ withdrawal / negative.
   * `NaN` if either AF7 or AF8 lacks a full, clean window.
   */
  valence: number;
}

/**
 * Compute the four cognitive indices from the current scope state.
 * Returns `null` if no channel has a full, clean {@link WINDOW} of
 * samples (first second of recording, or every electrode off the skin /
 * saturated — see {@link isCleanWindow}).
 *
 * Indices are computed per channel, then averaged (the same convention
 * as the Stanford code). FAA uses AF7/AF8 explicitly since it's the
 * left-vs-right asymmetry the formula requires.
 */
export function cognitiveIndices(
  scope: Scope,
  channels: string[],
): CognitiveIndices | null {
  const perChannel: { name: string; powers: ChannelPowers }[] = [];
  for (const channel of channels) {
    const series = scope.read(channel);
    if (series.length < WINDOW) continue;
    const start = series.length - WINDOW;
    if (!isCleanWindow(series, start)) continue;
    perChannel.push({
      name: channel,
      powers: powersForChannel(series, start),
    });
  }
  if (perChannel.length === 0) return null;

  let fSum = 0;
  let eSum = 0;
  let wSum = 0;
  let sSum = 0;
  let fatSum = 0;
  for (const { powers } of perChannel) {
    fSum += powers.beta / (powers.alpha + EPS);
    eSum += powers.beta / (powers.alpha + powers.theta + EPS);
    wSum += powers.theta / (powers.alpha + EPS);
    sSum += powers.betaHigh / (powers.alpha + EPS);
    fatSum += (powers.alpha + powers.theta) / (powers.beta + EPS);
  }
  const n = perChannel.length;

  const left = perChannel.find((c) => c.name === FAA_LEFT_CHANNEL);
  const right = perChannel.find((c) => c.name === FAA_RIGHT_CHANNEL);
  const valence =
    left && right
      ? Math.log(right.powers.alpha + EPS) - Math.log(left.powers.alpha + EPS)
      : NaN;

  return {
    focus: fSum / n,
    engagement: eSum / n,
    workload: wSum / n,
    stress: sSum / n,
    fatigue: fatSum / n,
    valence,
  };
}
